<template>
  <ErrorHandler />
  <div>
    <router-view :key="$route.fullPath" />
    <CookieConsent
      :preferences="cookiePreferences"
      @on-accept-all-cookies="onAcceptAllCookies"
      @on-save-cookie-preferences="onSaveCookiePreferences"
    />

    <NotificationGroup group="ndj" position="top">
      <div
        class="fixed z-[999999999999999] inset-0 flex items-end justify-center p-6 pointer-events-none"
      >
        <div class="w-full max-w-md sm:max-w-lg">
          <Notification
            v-slot="{ notifications, close, hovering }"
            enter="transform ease-out duration-300 transition"
            enter-from="$translate-y-4 opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transition ease-in duration-500"
            leave-from="opacity-100"
            leave-to="opacity-0"
            move="transition duration-500"
            move-delay="delay-150"
          >
            <div
              v-for="notification in notifications"
              :key="notification.id"
              class="relative mb-4 rounded-lg shadow-xl p-4 pointer-events-auto z-[999999999999999] transform transition-all duration-300 hover:scale-[1.02] backdrop-filter backdrop-blur-sm"
              @mouseover="
                !notification.closeable && hovering(notification.id, true, 1000)
              "
              @mouseleave="
                !notification.closeable &&
                  hovering(notification.id, false, 1000)
              "
              :class="{
                'cursor-pointer': notification.clickable,
                'bg-blue-100/95 border-l-4 border-blue-500 border-t border-r border-b border-blue-200':
                  notification.type === 'video',
                'bg-indigo-100/95 border-l-4 border-indigo-500 border-t border-r border-b border-indigo-200':
                  notification.type === 'invite',
                'bg-purple-100/95 border-l-4 border-purple-500 border-t border-r border-b border-purple-200':
                  notification.type === 'image',
                'bg-green-100/95 border-l-4 border-green-500 border-t border-r border-b border-green-200':
                  notification.type === 'download',
                'bg-yellow-100/95 border-l-4 border-yellow-500 border-t border-r border-b border-yellow-200':
                  notification.type === 'button',
                'bg-blue-50/95 border-l-4 border-blue-500 border-t border-r border-b border-blue-200':
                  notification.type === 'notification',
              }"
              @click="notification.clickable ? notification.onClick() : null"
            >
              <!-- Notification Content -->
              <div
                @click.stop="!notification.clickable || notification.onClick()"
                class="w-full"
              >
                <!-- Video Notification -->
                <div v-if="notification.type === 'video'">
                  <p
                    class="font-semibold text-xl text-blue-900 mb-4 flex items-center"
                  >
                    <font-awesome-icon icon="video" class="mr-2" />
                    {{ notification.title }}
                  </p>
                  <p class="text-sm text-blue-800 mb-4">
                    {{ notification.text }}
                  </p>
                  <div
                    class="relative w-full overflow-hidden rounded-md mb-4"
                    style="padding-top: 56.25%"
                  >
                    <iframe
                      :src="notification.videoUrl"
                      frameborder="0"
                      allowfullscreen
                      class="absolute top-0 left-0 w-full h-full"
                    ></iframe>
                  </div>
                  <div class="mt-4 flex justify-end">
                    <button
                      @click.stop="notification.onClick()"
                      class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm flex items-center"
                    >
                      {{ notification.button_text }}
                      <font-awesome-icon icon="arrow-right" class="ml-2" />
                    </button>
                  </div>
                </div>

                <!-- Invite Notification -->
                <div v-else-if="notification.type === 'invite'">
                  <p
                    class="font-semibold text-xl text-indigo-900 mb-4 flex items-center"
                  >
                    <font-awesome-icon icon="calendar-alt" class="mr-2" />
                    {{ notification.title }}
                  </p>
                  <p class="text-sm text-indigo-800 mb-4">
                    {{ notification.text }}
                  </p>
                  <img
                    :src="notification.image"
                    alt="Event Image"
                    class="w-full h-auto object-cover mb-4 rounded-lg"
                  />
                  <div class="mt-4 flex justify-end">
                    <button
                      v-if="notification.button_text"
                      @click.stop="notification.onClick()"
                      class="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-md shadow-sm flex items-center"
                    >
                      {{ notification.button_text }}
                      <font-awesome-icon icon="arrow-right" class="ml-2" />
                    </button>
                  </div>
                </div>

                <!-- Image Notification -->
                <div v-else-if="notification.type === 'image'">
                  <div class="flex items-center mb-3">
                    <div class="bg-purple-100 p-2 rounded-full mr-3">
                      <font-awesome-icon icon="image" class="text-purple-600 text-lg" />
                    </div>
                    <p class="font-semibold text-xl text-purple-900">
                      {{ notification.title }}
                    </p>
                  </div>
                  <p class="text-sm text-purple-700 mb-3 ml-1 leading-relaxed">
                    {{ notification.text }}
                  </p>
                  <div @click.stop="notification.onClick" class="relative overflow-hidden rounded-lg group cursor-pointer">
                    <img
                      :src="notification.image"
                      alt="Shared Image"
                      class="w-full h-auto object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                    />
                    <div class="absolute inset-0 bg-purple-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div class="bg-white/80 rounded-full p-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        <font-awesome-icon icon="external-link-alt" class="text-purple-600 text-xl" />
                      </div>
                    </div>
                  </div>
                  <div class="mt-4 flex justify-end w-full">
                    <button
                      @click.stop="notification.onClick()"
                      class="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2.5 rounded-md shadow-md flex items-center font-medium transition-all duration-300 hover:shadow-lg hover:translate-y-[-1px] focus:ring-2 focus:ring-purple-400 focus:ring-offset-2"
                    >
                      {{ notification.button_text || 'Open Link' }}
                      <font-awesome-icon icon="arrow-right" class="ml-2" />
                    </button>
                  </div>
                </div>

                <!-- Download Notification -->
                <div
                  v-else-if="notification.type === 'download'"
                  class="flex flex-col items-start"
                >
                  <p
                    class="font-semibold text-xl text-green-900 mb-4 flex items-center"
                  >
                    <font-awesome-icon
                      icon="download"
                      class="mr-2 text-green-600"
                    />
                    {{ notification.title }}
                  </p>
                  <p class="text-sm text-green-800 mb-4">
                    {{ notification.text }}
                  </p>
                  <button
                    @click.stop="notification.onClick()"
                    class="ml-auto bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-md shadow-sm flex items-center"
                  >
                    {{ notification.button_text }}
                    <font-awesome-icon icon="arrow-right" class="ml-2" />
                  </button>
                </div>

                <!-- Button Notification -->
                <div
                  v-else-if="notification.type === 'button'"
                  class="flex flex-col items-start"
                >
                  <p
                    class="font-semibold text-xl text-yellow-900 mb-4 flex items-center"
                  >
                    <font-awesome-icon
                      icon="exclamation-circle"
                      class="mr-2 text-yellow-600"
                    />
                    {{ notification.title }}
                  </p>
                  <p class="text-sm text-yellow-800 mb-4">
                    {{ notification.text }}
                  </p>
                  <button
                    @click.stop="notification.onClick()"
                    class="ml-auto bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 rounded-md shadow-sm flex items-center"
                  >
                    {{ notification.button_text }}
                    <font-awesome-icon icon="arrow-right" class="ml-2" />
                  </button>
                </div>

                <!-- General Notification -->
                <div v-else class="flex flex-col items-start">
                  <img
                    v-if="notification.image"
                    :src="notification.image"
                    alt="Notification Image"
                    class="w-full h-auto object-cover mb-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300"
                  />
                  <div class="flex items-center mb-3">
                    <div class="bg-blue-100 p-2 rounded-full mr-3">
                      <font-awesome-icon icon="bell" class="text-blue-600 text-lg" />
                    </div>
                    <p class="font-semibold text-xl text-gray-900">
                      {{ notification.title }}
                    </p>
                  </div>
                  <p class="text-sm text-gray-700 mb-4 ml-1 leading-relaxed">
                    {{ notification.text }}
                  </p>
                  <div
                    v-if="notification.clickable"
                    class="mt-4 flex justify-end w-full"
                  >
                    <button
                      @click.stop="notification.onClick()"
                      class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-md shadow-md flex items-center font-medium transition-all duration-300 hover:shadow-lg hover:translate-y-[-1px] focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                    >
                      {{ notification.button_text }}
                      <font-awesome-icon icon="arrow-right" class="ml-2 animate-pulse" />
                    </button>
                  </div>
                </div>
              </div>

              <!-- Close Button -->
              <button
                v-if="notification.closeable"
                @click.stop="close(notification.id)"
                class="absolute top-2 right-2 text-gray-500 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-white/80 hover:bg-white rounded-full w-6 h-6 flex items-center justify-center shadow-sm hover:shadow transition-all duration-200"
                aria-label="Close notification"
              >
                <font-awesome-icon icon="times" size="sm" />
              </button>
            </div>
          </Notification>
        </div>
      </div>
    </NotificationGroup>

    <NotificationGroup group="error">
      <div
        class="fixed z-[999999999999999] inset-0 flex items-start justify-center p-6 px-4 pt-10 pointer-events-none"
      >
        <div class="w-full max-w-md">
          <Notification
            v-slot="{ notifications }"
            enter="transform ease-out duration-300 transition"
            enter-from="translate-y-[-16px] opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transition ease-in duration-500"
            leave-from="opacity-100"
            leave-to="opacity-0"
            move="transition duration-500"
            move-delay="delay-300"
          >
            <div
              class="modern-notification error w-full mx-auto mt-4 overflow-hidden"
              v-for="notification in notifications"
              :key="notification.id"
            >
              <div class="notification-content">
                <div class="notification-icon">
                  <font-awesome-icon icon="exclamation-circle" />
                </div>
                <div class="notification-text">
                  <div class="notification-title">
                    {{ notification.title || "Error" }}
                  </div>
                  <div class="notification-message">
                    {{ notification.text }}
                  </div>
                </div>
              </div>
            </div>
          </Notification>
        </div>
      </div>
    </NotificationGroup>

    <NotificationGroup group="info">
      <div
        class="fixed z-[999999999999999] inset-0 flex items-start justify-center p-6 px-4 pt-10 pointer-events-none"
      >
        <div class="w-full max-w-md">
          <Notification
            v-slot="{ notifications }"
            enter="transform ease-out duration-300 transition"
            enter-from="translate-y-[-16px] opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transition ease-in duration-500"
            leave-from="opacity-100"
            leave-to="opacity-0"
            move="transition duration-500"
            move-delay="delay-300"
          >
            <div v-for="notification in notifications" :key="notification.id">
              <!-- Info notification -->
              <div
                v-if="notification.type === 'info' || !notification.type"
                class="modern-notification info w-full mx-auto mt-4"
              >
                <div class="notification-content">
                  <div class="notification-icon">
                    <font-awesome-icon icon="info-circle" />
                  </div>
                  <div class="notification-text">
                    <div class="notification-title">
                      {{ notification.title || "Info" }}
                    </div>
                    <div class="notification-message">
                      {{ notification.text }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Success notification -->
              <div
                v-if="notification.type === 'success'"
                class="modern-notification success w-full mx-auto mt-4"
              >
                <div class="notification-content">
                  <div class="notification-icon">
                    <font-awesome-icon icon="check-circle" />
                  </div>
                  <div class="notification-text">
                    <div class="notification-title">
                      {{ notification.title || "Success" }}
                    </div>
                    <div class="notification-message">
                      {{ notification.text }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Warning notification -->
              <div
                v-if="notification.type === 'warning'"
                class="modern-notification warning w-full mx-auto mt-4"
              >
                <div class="notification-content">
                  <div class="notification-icon">
                    <font-awesome-icon icon="exclamation-triangle" />
                  </div>
                  <div class="notification-text">
                    <div class="notification-title">
                      {{ notification.title || "Warning" }}
                    </div>
                    <div class="notification-message">
                      {{ notification.text }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Notification>
        </div>
      </div>
    </NotificationGroup>

    <NotificationGroup group="generic">
      <div
        class="fixed z-[999999999999999] inset-0 flex items-start justify-center p-6 px-4 pt-10 pointer-events-none"
      >
        <div class="w-full max-w-md">
          <Notification
            v-slot="{ notifications }"
            enter="transform ease-out duration-300 transition"
            enter-from="translate-y-[-16px] opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transition ease-in duration-500"
            leave-from="opacity-100"
            leave-to="opacity-0"
            move="transition duration-500"
            move-delay="delay-300"
          >
            <div
              class="flex w-full max-w-sm mx-auto mt-4 overflow-hidden bg-white rounded-lg shadow-md"
              v-for="notification in notifications"
              :key="notification.id"
            >
              <div class="flex items-center justify-center w-12 bg-blue-500">
                <svg
                  class="w-6 h-6 text-white fill-current"
                  viewBox="0 0 40 40"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 3.33331C10.8 3.33331 3.33337 10.8 3.33337 20C3.33337 29.2 10.8 36.6666 20 36.6666C29.2 36.6666 36.6667 29.2 36.6667 20C36.6667 10.8 29.2 3.33331 20 3.33331ZM21.6667 28.3333H18.3334V25H21.6667V28.3333ZM21.6667 21.6666H18.3334V11.6666H21.6667V21.6666Z"
                  />
                </svg>
              </div>

              <div class="px-4 py-2 -mx-3">
                <div class="mx-3">
                  <span class="font-semibold text-blue-500"
                    >{{ notification.title }}Info</span
                  >
                  <p class="text-sm text-gray-600">{{ notification.text }}</p>
                </div>
              </div>
            </div>
          </Notification>
        </div>
      </div>
    </NotificationGroup>

    <NotificationGroup group="userActivity" position="bottom">
      <div
        class="fixed z-[999999999999999] inset-0 flex items-end justify-end p-6 px-4 py-6 pointer-events-none"
      >
        <div class="w-full max-w-sm">
          <Notification
            v-slot="{ notifications }"
            enter="transform ease-out duration-300 transition"
            enter-from="$translate-y-4 opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transition ease-in duration-500"
            leave-from="opacity-100"
            leave-to="opacity-0"
            move="transition"
          >
            <!-- Added space-y-3 for vertical spacing between notifications -->
            <div class="space-y-3">
              <div
                v-for="notification in notifications"
                :key="notification.id"
                class="flex items-center w-full max-w-sm p-4 bg-white rounded-lg shadow-md border border-gray-200 space-x-4"
              >
                <img
                  :src="notification.avatar"
                  alt="User Avatar"
                  class="w-10 h-10 object-cover rounded-full border border-gray-300"
                />
                <div class="flex flex-col">
                  <p class="font-medium text-gray-900">
                    {{ notification.message }}
                  </p>
                </div>
              </div>
            </div>
          </Notification>
        </div>
      </div>
    </NotificationGroup>

    <NotificationGroup group="moderatorActivity" position="bottom-left">
      <div
        class="fixed z-[999999999999999] inset-0 flex items-end justify-start p-6 px-4 py-6 pointer-events-none"
      >
        <div class="w-full max-w-sm">
          <Notification
            v-slot="{ notifications }"
            enter="transform ease-out duration-300 transition"
            enter-from="$translate-y-4 opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transition ease-in duration-500"
            leave-from="opacity-100"
            leave-to="opacity-0"
            move="transition"
          >
            <!-- Added space-y-3 for vertical spacing between notifications -->
            <div class="space-y-3">
              <div v-for="notification in notifications" :key="notification.id">
                <!-- Moderator "counter" notification (with chart icon) -->
                <div
                  v-if="notification.type === 'counter'"
                  class="flex items-center w-full max-w-sm p-4 bg-white rounded-lg shadow-md border border-gray-200 space-x-4"
                >
                  <!-- Chart Icon -->
                  <div
                    class="w-12 h-12 flex items-center justify-center bg-blue-500 rounded-full"
                  >
                    <svg
                      class="w-6 h-6 text-white fill-current"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M2 11a1 1 0 011-1h14a1 1 0 110 2H3a1 1 0 01-1-1zM2 7a1 1 0 011-1h10a1 1 0 110 2H3a1 1 0 01-1-1zM2 15a1 1 0 011-1h6a1 1 0 110 2H3a1 1 0 01-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                  <!-- Notification Text -->
                  <div class="flex flex-col">
                    <p class="font-medium text-gray-900">
                      {{ notification.text }}
                    </p>
                  </div>
                </div>

                <!-- Info Notification -->
                <div
                  v-if="notification.type === 'info'"
                  class="flex items-center w-full max-w-sm p-4 bg-white rounded-lg shadow-md border border-gray-200 space-x-4"
                >
                  <!-- Conditional Icon or Image -->
                  <div
                    class="w-12 h-12 flex items-center justify-center bg-green-500 rounded-full"
                  >
                    <template v-if="notification.image">
                      <img
                        :src="notification.image"
                        alt="Notification Image"
                        class="w-12 h-12 rounded-full"
                      />
                    </template>
                    <template v-else>
                      <svg
                        class="w-6 h-6 text-white fill-current"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 11.707a1 1 0 001.414 0L13 8.828V11a1 1 0 102 0V7a1 1 0 00-1-1h-4a1 1 0 100 2h2.172l-2.293 2.293a1 1 0 000 1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </template>
                  </div>
                  <!-- Notification Text -->
                  <div class="flex flex-col">
                    <p class="font-medium text-gray-900">
                      {{ notification.text }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Notification>
        </div>
      </div>
    </NotificationGroup>
  </div>
</template>

<script>
import { cookie } from "@/utils/cookie"; // Import the functions
import ErrorHandler from "./components/general/ErrorHandler.vue"; // Ensure correct path
import CookieConsent from "./components/cookie/CookieConsent.vue";

export default {
  name: "App",
  components: {
    ErrorHandler,
    CookieConsent,
  },
  data() {
    return {
      cookiePreferences: [
        {
          title: "Technické a tedy nezbytné cookies",
          description:
            "Jsou nutnou podmínkou pro fungování našeho webu. Díky nim procházíte jednotlivými stránkami, vyhledáváte nebo se přihlašujete.",
          items: [
            {
              label: "Technické cookies",
              value: "technical",
              isRequired: true,
            },
          ],
        },
        {
          title: "Statistické cookies",
          description:
            'Díky těmto cookies víme, které předpisy a dokumenty jsou u nás navštěvované. To nám umožňuje dávat Vám doporučení „na míru". Součástí jsou i cookies třetích stran, jejichž vydavatelem je např. Google.',
          items: [{ label: "Statistické cookies", value: "statistics" }],
        },
        {
          title: "Marketingové cookies",
          description:
            "Tyto cookies sledují Váš pohyb na webu. Slouží zejména k zobrazování relevantních reklam a k využítí Vašich sociálních sítí. Rovněž zde to mohou být cookies třetích stran, jejichž vydavatelem jsou instituce jako Google, Facebook a další.",
          items: [{ label: "Marketingové cookies", value: "marketing" }],
        },
      ],
    };
  },
  methods: {
    onAcceptAllCookies() {
      // Set all cookies
      const settings = window.vueAppConfig?.settings ?? {};
      const cookieName = settings.cookieName ?? "ne_twork";
      cookie(cookieName); // This will set the cookie if it doesn't exist

      // You can add custom cookie handling here
      console.log("All cookies accepted");
    },
    onSaveCookiePreferences(preferences) {
      // Handle saved preferences
      console.log("User preferences saved:", preferences);

      // Set essential cookies
      const settings = window.vueAppConfig?.settings ?? {};
      const cookieName = settings.cookieName ?? "ne_twork";
      cookie(cookieName);

      // You can handle specific cookie categories here based on preferences array
    },
  },
};
</script>


<style>
.notification-container {
  z-index: 2147483647 !important;
}

/* Modern Appearance for App */
:root {
  --primary: #6366f1;
  --primary-light: #818cf8;
  --primary-dark: #4f46e5;
  --success: #10b981;
  --info: #3b82f6;
  --warning: #f59e0b;
  --error: #ef4444;
  --background: #f9fafb;
  --card: #ffffff;
  --text: #111827;
  --text-secondary: #6b7280;
  --border: #e5e7eb;
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 6px 16px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.1);
  --radius-sm: 0.375rem;
  --radius: 0.5rem;
  --radius-md: 0.75rem;
  --radius-lg: 1rem;
  --transition: all 0.2s ease;
}

/* Global transitions */
* {
  transition: background-color 0.2s ease, border-color 0.2s ease,
    box-shadow 0.2s ease;
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

body {
  background-color: var(--background);
  color: var(--text);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
}

/* Enhance buttons throughout the app */
button,
.btn,
a[role="button"] {
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

button:focus,
.btn:focus,
a[role="button"]:focus {
  outline: 2px solid rgba(99, 102, 241, 0.5);
  outline-offset: 2px;
}

/* Modern notification styles */
.notifications .notification {
  border-radius: var(--radius);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  transform-origin: top center;
}

.notification-enter-active {
  animation: notification-in 0.3s ease-out forwards;
}

.notification-leave-active {
  animation: notification-out 0.3s ease-in forwards;
}

@keyframes notification-in {
  0% {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes notification-out {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
}

/* Modern notification styles */
.modern-notification {
  background-color: white;
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: notification-in 0.3s ease-out forwards;
  position: relative;
  border-left: 6px solid transparent;
  max-width: 500px;
  width: 100%;
  margin-bottom: 1rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.modern-notification:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-xl, 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04));
}

.notification-content {
  display: flex;
  padding: 1.25rem;
  align-items: flex-start;
}

.notification-icon {
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.25rem;
  font-size: 1.25rem;
}

.notification-text {
  flex: 1;
}

.notification-title {
  font-weight: 600;
  font-size: 1.125rem;
  margin-bottom: 0.375rem;
}

.notification-message {
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.4;
}

/* Error notification */
.modern-notification.error {
  border-left-color: var(--error);
}

.modern-notification.error .notification-icon {
  background-color: rgba(239, 68, 68, 0.1);
  color: var(--error);
}

.modern-notification.error .notification-title {
  color: var(--error);
}

/* Success notification */
.modern-notification.success {
  border-left-color: var(--success);
}

.modern-notification.success .notification-icon {
  background-color: rgba(16, 185, 129, 0.1);
  color: var(--success);
}

.modern-notification.success .notification-title {
  color: var(--success);
}

/* Info notification */
.modern-notification.info {
  border-left-color: var(--info);
}

.modern-notification.info .notification-icon {
  background-color: rgba(59, 130, 246, 0.1);
  color: var(--info);
}

.modern-notification.info .notification-title {
  color: var(--info);
}

/* Warning notification */
.modern-notification.warning {
  border-left-color: var(--warning);
}

.modern-notification.warning .notification-icon {
  background-color: rgba(245, 158, 11, 0.1);
  color: var(--warning);
}

.modern-notification.warning .notification-title {
  color: var(--warning);
}

/* Focus styles for accessibility */
:focus-visible {
  outline: 2px solid rgba(99, 102, 241, 0.5);
  outline-offset: 2px;
}

/* Improved scrollbars for modern browsers */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>